import { useMutation } from '@apollo/client';
import { CreateOrganisationWebhookDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type {
  CreateWebhookInput,
  CreateWebhookResult,
} from './useCreateWebhookTRPC';
import { useCreateWebhookTRPC } from './useCreateWebhookTRPC';

export const useCreateWebhook = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [createWebhookGraphQL, graphqlState] = useMutation(
    CreateOrganisationWebhookDocument,
    {
      update: (cache) => {
        evictField(cache, 'organisation_webhook');
      },
    }
  );

  const trpcMutation = useCreateWebhookTRPC();

  const createWebhook = async (
    input: CreateWebhookInput
  ): Promise<CreateWebhookResult> => {
    if (trpcEnabled) {
      return trpcMutation.createWebhook(input);
    }

    const result = await createWebhookGraphQL({ variables: { object: input } });
    const plaintextSecret =
      result.data?.createOrganisationWebhook?.SigningSecret;
    if (!plaintextSecret) {
      throw new Error('Failed to create webhook');
    }

    return { plaintextSecret };
  };

  if (trpcEnabled) {
    return {
      createWebhook,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    createWebhook,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
